import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  Typography,
  Paper,
} from "@mui/material";
import {
  Close,
  OpenInNewOutlined,
  ThumbUpOutlined,
  ChatBubbleOutlineOutlined,
  VisibilityOffOutlined,
} from "@mui/icons-material";
import { MentionResult, SuggestedReplies } from "../types";
import PlatformIcon from "../components/PlatformIcon";
import ReplyGenerator from "../components/ReplyGenerator";
import { MENTION_STATUS_DISMISSED } from "../../../constants";
import moment from "moment";

interface MentionDetailDialogProps {
  open: boolean;
  onClose: () => void;
  mention: MentionResult | null;
  onDismiss: (mention: MentionResult) => Promise<void>;
  onGenerateReplies: (
    mentionId: string,
    context: string
  ) => Promise<SuggestedReplies>;
}

const MentionDetailDialog: React.FC<MentionDetailDialogProps> = ({
  open,
  onClose,
  mention,
  onDismiss,
  onGenerateReplies,
}) => {
  const [dismissing, setDismissing] = useState(false);

  if (!mention) return null;

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await onDismiss(mention);
    } finally {
      setDismissing(false);
    }
  };

  const handleGenerateReplies = async (context: string) => {
    return onGenerateReplies(mention.id, context);
  };

  const getSentimentColor = () => {
    switch (mention.sentiment) {
      case "positive":
        return "success";
      case "negative":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <PlatformIcon platform={mention.platform} size={20} />
              {mention.subreddit && (
                <Link
                  href={`https://reddit.com/r/${mention.subreddit}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  color="text.secondary"
                >
                  r/{mention.subreddit}
                </Link>
              )}
              <Chip
                size="small"
                label={mention.status}
                color={
                  mention.status === "new"
                    ? "primary"
                    : mention.status === "seen"
                    ? "default"
                    : "default"
                }
                sx={{ height: 20, fontSize: "0.7rem", textTransform: "capitalize" }}
              />
              {mention.sentiment && (
                <Chip
                  size="small"
                  label={mention.sentiment}
                  color={getSentimentColor()}
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.7rem", textTransform: "capitalize" }}
                />
              )}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {mention.title || "(No title)"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            {mention.authorUsername && (
              <Typography variant="body2" color="text.secondary">
                by{" "}
                {mention.authorUrl ? (
                  <Link
                    href={mention.authorUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {mention.authorUsername}
                  </Link>
                ) : (
                  mention.authorUsername
                )}
              </Typography>
            )}
            {mention.metrics.upvotes !== undefined && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ThumbUpOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {mention.metrics.upvotes}
                </Typography>
              </Box>
            )}
            {mention.metrics.points !== undefined && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ThumbUpOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {mention.metrics.points} points
                </Typography>
              </Box>
            )}
            {mention.metrics.comments !== undefined && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ChatBubbleOutlineOutlined
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {mention.metrics.comments} comments
                </Typography>
              </Box>
            )}
            {mention.postedAt && (
              <Typography variant="body2" color="text.secondary">
                Posted {moment(mention.postedAt).fromNow()}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            {mention.matchedKeywords.map((keyword) => (
              <Chip
                key={keyword}
                label={keyword}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>

          <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewOutlined />}
              href={mention.postUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Original
            </Button>
            {mention.status !== MENTION_STATUS_DISMISSED && (
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                startIcon={<VisibilityOffOutlined />}
                onClick={handleDismiss}
                disabled={dismissing}
              >
                {dismissing ? "Dismissing..." : "Dismiss"}
              </Button>
            )}
          </Box>
        </Box>

        {mention.content && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: "action.hover",
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Post Content
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.6,
              }}
            >
              {mention.content}
            </Typography>
          </Paper>
        )}

        <Divider sx={{ my: 3 }} />

        <ReplyGenerator
          onGenerate={handleGenerateReplies}
          initialReplies={mention.suggestedReplies}
        />
      </DialogContent>
    </Dialog>
  );
};

export default MentionDetailDialog;
